import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** One line of the month-end report (logixi-be warehouse-storage-charges/report). */
export interface StorageChargeLine {
  accountId: number;
  accountName: string;
  productName: string;
  sku: string | null;
  batchNumber: string | null;
  unitName: string;
  chargeDays: number;
  quantityDays: number;
  storageRate: number | null;
  storageAmount: number;
  rhdQty: number;
  rhdRate: number | null;
  rhdAmount: number;
  noRate: boolean;
}

/**
 * Month-end storage and RH&D charges for every customer's owned stock. Customers see their own lines
 * of exactly this report in the customer portal.
 */
@Component({
    selector: 'app-storage-charges',
    templateUrl: './storage-charges.component.html',
    styleUrls: ['./storage-charges.component.scss'],
    standalone: false
})
export class StorageChargesComponent implements OnInit {

  month: string = new Date().toISOString().slice(0, 7);
  accountId: number = -1;

  lines: StorageChargeLine[] = [];
  extraDays: number = 1;
  loading: boolean = false;
  isServerError: boolean = false;

  constructor(private httpClient: HttpClient) { }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.isServerError = false;

    this.httpClient.post(environment.api.endpoint + 'warehouse-storage-charges/report', { month: this.month })
    .pipe(take(1))
    .subscribe({
      next: (report: any) => {
        this.lines = report.lines;
        this.extraDays = report.extraDays;
        if (!this.customers().some(c => c.id === this.accountId)) {
          this.accountId = -1;
        }
        this.loading = false;
      },
      error: () => {
        this.isServerError = true;
        this.loading = false;
      }
    });
  }

  customers(): { id: number, name: string }[] {
    const seen = new Map<number, string>();
    this.lines.forEach(l => seen.set(l.accountId, l.accountName));
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }

  visibleLines(): StorageChargeLine[] {
    return this.accountId === -1 ? this.lines : this.lines.filter(l => l.accountId === this.accountId);
  }

  /** Lines with no rate are shown but never totalled - storage is not silently priced at zero. */
  total(field: 'storageAmount' | 'rhdAmount'): number {
    return Math.round(this.visibleLines().filter(l => !l.noRate).reduce((sum, l) => sum + l[field], 0) * 100) / 100;
  }

  exportCsv(): void {
    const header = ['Customer', 'Product', 'SKU', 'Batch', 'Unit', 'Days', 'Qty days', 'Storage rate', 'Storage',
      'RH&D qty', 'RH&D rate', 'RH&D'];
    const rows = this.visibleLines().map(l => [l.accountName, l.productName, l.sku || '', l.batchNumber || '', l.unitName,
      l.chargeDays, l.quantityDays, l.storageRate === null ? 'No rate' : l.storageRate, l.storageAmount,
      l.rhdQty, l.rhdRate === null ? (l.rhdQty ? 'No rate' : '') : l.rhdRate, l.rhdAmount]);
    const csv = [header, ...rows].map(r => r.map(cell => csvCell(cell)).join(',')).join('\r\n');

    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    link.download = 'storage-charges-' + this.month + '.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }
}

/** Quoted when needed; a leading = + - @ is neutralised so a spreadsheet never runs it as a formula. */
function csvCell(value: string | number): string {
  let text = String(value);
  if (typeof value === 'string' && /^[=+\-@]/.test(text)) {
    text = "'" + text;
  }
  return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}
